import React from "react";
import Image, { type StaticImageData } from "next/image";
import { Box, AspectRatio, Flex, Text } from "@chakra-ui/react";
import { Section, type SectionProps } from "components/section";
import { SectionHeading } from "components/section-heading";
import { ArrowLink } from "ui/link";
import customerStudioImage from "../../../public/assets/composable-cdp-diagram/customer-studio.png";
import eventsImage from "../../../public/assets/composable-cdp-diagram/events.png";
import identityResolutionImage from "../../../public/assets/composable-cdp-diagram/identity-resolution.png";
import campaignIntelligenceImage from "../../../public/assets/composable-cdp-diagram/campaign-intelligence.png";
import reverseEtlImage from "../../../public/assets/composable-cdp-diagram/reverse-etl.png";
import matchBoosterImage from "../../../public/assets/composable-cdp-diagram/match-booster.png";
import personalizationApiImage from "../../../public/assets/composable-cdp-diagram/personalization-api.png";
import { CheckHeading } from "./check-heading";

type Variant =
  | "events"
  | "identity-resolution"
  | "campaign-intelligence"
  | "reverse-etl"
  | "customer-studio"
  | "match-booster"
  | "personalization-api";

const variantImageMap: { [key in Variant]: StaticImageData } = {
  events: eventsImage,
  "identity-resolution": identityResolutionImage,
  "campaign-intelligence": campaignIntelligenceImage,
  "reverse-etl": reverseEtlImage,
  "customer-studio": customerStudioImage,
  "match-booster": matchBoosterImage,
  "personalization-api": personalizationApiImage,
};

type CdpDiagramProps = {
  variant: Variant;
  sectionTitle: string;
};

export function ComposableCdpDiagram({ variant, sectionTitle, ...props }: CdpDiagramProps & SectionProps) {
  const image = variantImageMap[variant];

  return (
    <Section center {...props}>
      <SectionHeading eyebrow="The Composable CDP" title={sectionTitle} maxW={{ base: "3xl", lg: "auto" }} />

      <Flex
        align="center"
        direction={{ base: "column", lg: "row" }}
        gap={{ base: 2, md: 8, lg: 16, xl: 20 }}
        w="100%"
        maxW={{ base: "600px", lg: "100%" }}
        mx="auto"
        mt={{ base: 12, md: 16 }}
      >
        <AspectRatio
          ratio={3 / 2}
          flex={{ base: 1, lg: 1.2, xl: 1.4 }}
          pos="relative"
          w="100%"
          maxW={{ base: "500px", md: "100%" }}
          mx="auto"
        >
          <Image
            fill
            src={image}
            alt="Hightouch Composable CDP Diagram."
            sizes="100%"
            style={{ objectFit: "contain" }}
          />
        </AspectRatio>

        <Flex align="start" direction="column" gap={9} flex={1}>
          <Box>
            <CheckHeading>Use your existing data</CheckHeading>
            <Text mt={4}>
              Leverage all of the customer data you already have in your data warehouse for your marketing.
            </Text>
          </Box>

          <Box>
            <CheckHeading>Pick only the products you need</CheckHeading>
            <Text mt={4}>
              With a Composable CDP, you can choose only the products you need - like an audience builder.
            </Text>
          </Box>

          <ArrowLink href="/platform/composable-cdp" variant="grass" size={{ base: "md", md: "lg" }}>
            Learn more about Composable CDPs
          </ArrowLink>
        </Flex>
      </Flex>
    </Section>
  );
}
